import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, Redirect } from "react-router-dom";
import { Button, Chip, CircularProgress, makeStyles, MenuItem, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography } from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import CallLog from "../../components/CallLog";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  filters: { display: "flex", flexWrap: "wrap", gap: theme.spacing(2), padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  search: { flex: 1, minWidth: 220 },
  status: { minWidth: 180 },
  paper: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 },
  table: { flex: 1 },
  actions: { minWidth: 215 },
  message: { padding: theme.spacing(3), textAlign: "center" }
}));
const recordingLabels = { ready: "Disponível", processing: "Preparando", pending: "Aguardando", failed: "Falhou", unavailable: "Sem gravação" };
const transcriptLabels = { ready: "Disponível", processing: "Processando", queued: "Na fila", pending: "Aguardando", failed: "Falhou", unavailable: "Indisponível" };
const callLabels = { starting: "Iniciando", ringing: "Chamando", connected: "Em andamento", ended: "Encerrada", not_connected: "Não conectada", interrupted: "Interrompida" };
const duration = seconds => `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

function CallsList() {
  const classes = useStyles();
  const [calls, setCalls] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [recordingStatus, setRecordingStatus] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/calls", { params: {
          pageNumber: page + 1, pageSize, searchParam: search, recordingStatus
        } });
        if (!active) return;
        const lastPage = Math.max(0, Math.ceil(data.count / pageSize) - 1);
        if (page > lastPage) { setPage(lastPage); return; }
        setCalls(data.calls);
        setCount(data.count);
      } catch (_) {
        if (active) { setCalls([]); setCount(0); setError("Não foi possível carregar as ligações. Tente atualizar a lista."); }
      } finally { if (active) setLoading(false); }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [page, pageSize, search, recordingStatus, refresh]);

  return <MainContainer>
    <MainHeader>
      <Title>Ligações</Title>
      <Button startIcon={<RefreshIcon />} onClick={() => setRefresh(value => value + 1)} disabled={loading}>Atualizar</Button>
    </MainHeader>
    <Paper variant="outlined" className={classes.filters}>
      <TextField id="calls-search" label="Buscar contato, telefone, atendente ou ticket" variant="outlined" size="small"
        className={classes.search} value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} />
      <TextField id="calls-recording-status" select label="Gravação" variant="outlined" size="small" className={classes.status}
        value={recordingStatus} onChange={event => { setRecordingStatus(event.target.value); setPage(0); }}>
        <MenuItem value="">Todas as ligações</MenuItem>
        {Object.entries(recordingLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
      </TextField>
    </Paper>
    <Paper variant="outlined" className={classes.paper}>
      <TableContainer className={classes.table}>
        <Table size="small" aria-label="Ligações" stickyHeader>
          <TableHead><TableRow>
            {["Data e hora", "Contato", "Atendente", "Fila", "Duração", "Status", "Gravação", "Transcrição", "Ações"].map(label => <TableCell key={label}>{label}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={9} className={classes.message}><CircularProgress size={26} aria-label="Carregando ligações" /></TableCell></TableRow> :
              error ? <TableRow><TableCell colSpan={9}><Typography role="alert" color="error" className={classes.message}>{error}</Typography></TableCell></TableRow> :
              calls.length === 0 ? <TableRow><TableCell colSpan={9} className={classes.message}>Nenhuma ligação encontrada.</TableCell></TableRow> :
              calls.map(call => <TableRow key={call.id} hover>
                <TableCell>{new Date(call.createdAt).toLocaleString("pt-BR")}</TableCell>
                <TableCell><Typography variant="body2">{call.ticket.contact?.name || "Contato"}</Typography><Typography variant="caption" color="textSecondary">{call.ticket.contact?.number}</Typography></TableCell>
                <TableCell>{call.userName || "—"}</TableCell>
                <TableCell>{call.ticket.queue?.name || "Sem fila"}</TableCell>
                <TableCell>{duration(call.durationSeconds)}</TableCell>
                <TableCell>{callLabels[call.status] || call.status}</TableCell>
                <TableCell><Chip size="small" variant="outlined" color={call.recordingStatus === "ready" ? "primary" : "default"} label={recordingLabels[call.recordingStatus] || call.recordingStatus} /></TableCell>
                <TableCell>{transcriptLabels[call.transcriptionStatus] || call.transcriptionStatus}</TableCell>
                <TableCell className={classes.actions}>
                  <Button size="small" color="primary" component={RouterLink} to={`/tickets/${call.ticketId}`}>Abrir ticket #{call.ticketId}</Button>
                  <CallLog compact message={{ id: `platform-call-${call.id}`, ticketId: call.ticketId,
                    body: `${call.ticket.contact?.name || "Contato"} · ${call.userName || "Atendente"} · ${duration(call.durationSeconds)} · ${new Date(call.createdAt).toLocaleString("pt-BR")}` }} />
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={count} page={page} rowsPerPage={pageSize} rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(_, next) => setPage(next)} onRowsPerPageChange={event => { setPageSize(Number(event.target.value)); setPage(0); }}
        labelRowsPerPage="Por página:" labelDisplayedRows={({ from, to, count: total }) => `${from}–${to} de ${total}`}
        backIconButtonText="Página anterior" nextIconButtonText="Próxima página"
        backIconButtonProps={{ disabled: loading || page === 0 }} nextIconButtonProps={{ disabled: loading || (page + 1) * pageSize >= count }} />
    </Paper>
  </MainContainer>;
}

export default function Calls() {
  const { user, loading } = useContext(AuthContext);
  if (loading) return null;
  if (user?.profile !== "admin") return <Redirect to="/" />;
  return <CallsList />;
}
